import type { DisplayConversation } from "./ChatPage";

export function queueRevision(conversation: DisplayConversation) {
  const revision = conversation.composerDefaults?.queueRevision;
  return typeof revision === "number" ? revision : 0;
}

export function applyQueueSnapshot<T extends DisplayConversation | null>(
  current: T,
  conversationId: string,
  queue: unknown[],
  revision?: number,
): T {
  if (!current || current.id !== conversationId) return current;
  const incomingRevision = revision ?? 0;
  if (incomingRevision < queueRevision(current)) return current;
  return {
    ...current,
    composerDefaults: {
      ...(current.composerDefaults ?? {}),
      queue,
      queueRevision: incomingRevision,
    },
  } as T;
}

export function preserveNewerQueue(
  current: DisplayConversation | null,
  incoming: DisplayConversation,
) {
  if (
    !current ||
    current.id !== incoming.id ||
    queueRevision(incoming) >= queueRevision(current)
  )
    return incoming;
  return {
    ...incoming,
    composerDefaults: {
      ...incoming.composerDefaults,
      queue: current.composerDefaults?.queue,
      queueRevision: queueRevision(current),
    },
  };
}

/** Restore durable execution, including the gap between successive queue jobs. */
export function conversationFollowTarget(conversation: DisplayConversation) {
  if (
    ["awaiting-approval", "awaiting-question"].includes(
      conversation.generationStatus ?? "",
    )
  )
    return null;
  if (conversation.activeGenerationMessageId)
    return conversation.activeGenerationMessageId;
  const running = conversation.messages?.find(
    (message) =>
      message.role === "assistant" && message.generationStatus === "running",
  );
  if (running) return running.id;
  const queue = conversation.composerDefaults?.queue;
  if (Array.isArray(queue) && queue.some((item) => item && !item.error))
    return (
      [...(conversation.messages ?? [])]
        .reverse()
        .find((message) => message.role === "assistant")?.id ?? null
    );
  return null;
}

export function orderedQueueItems<
  T extends { error?: string; steer?: boolean },
>(items: T[]) {
  return [...items].sort(
    (a, b) =>
      Number(Boolean(a.error)) - Number(Boolean(b.error)) ||
      Number(Boolean(b.steer)) - Number(Boolean(a.steer)),
  );
}
